<template>
  <div class="admin-pay-fee-detail-container">
    <el-row :gutter="16">
      <!-- 左侧小区树：保留原有组件与事件，不改任何业务逻辑 -->
      <el-col :xs="24" :sm="24" :md="5" :lg="4">
        <select-admin-community ref="selectCommunity" @changeCommunity="handleCommunityChange" />
      </el-col>

      <el-col :xs="24" :sm="24" :md="19" :lg="20">
        <!-- (1) PageHeader 标题 + 右侧主动作 -->
        <ds-page-header
          :title="$t('adminPayFeeDetail.page.title')"
          :subtitle="$t('adminPayFeeDetail.page.subtitle')">
          <template #extra>
            <el-button type="primary" size="small" icon="el-icon-download" @click="handleExport">
              {{ $t('common.export') }}
            </el-button>
          </template>
        </ds-page-header>

        <!-- (2) SearchPanel 筛选区：字段顺序保持不变，仅重排栅格与按钮位置 -->
        <ds-search-panel>
          <el-row :gutter="16">
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-date-picker v-model="searchForm.startTime" type="datetime" value-format="yyyy-MM-dd HH:mm:ss"
                :placeholder="$t('adminPayFeeDetail.search.startTimePlaceholder')" class="w-full" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-date-picker v-model="searchForm.endTime" type="datetime" value-format="yyyy-MM-dd HH:mm:ss"
                :placeholder="$t('adminPayFeeDetail.search.endTimePlaceholder')" class="w-full" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-select v-model="searchForm.primeRate"
                :placeholder="$t('adminPayFeeDetail.search.paymentMethodPlaceholder')" clearable class="w-full">
                <el-option v-for="item in paymentMethods" :key="item.statusCd" :label="item.name"
                  :value="item.statusCd" />
              </el-select>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-select v-model="searchForm.state" :placeholder="$t('adminPayFeeDetail.search.feeStatusPlaceholder')"
                clearable class="w-full">
                <el-option v-for="item in feeStatuses" :key="item.statusCd" :label="item.name"
                  :value="item.statusCd" />
              </el-select>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-select v-model="searchForm.feeTypeCd"
                :placeholder="$t('adminPayFeeDetail.search.feeTypePlaceholder')" clearable class="w-full">
                <el-option v-for="item in feeTypes" :key="item.statusCd" :label="item.name" :value="item.statusCd" />
              </el-select>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-date-picker v-model="searchForm.feeStartTime" type="datetime" value-format="yyyy-MM-dd HH:mm:ss"
                :placeholder="$t('adminPayFeeDetail.search.feeStartTimePlaceholder')" class="w-full" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-date-picker v-model="searchForm.feeEndTime" type="datetime" value-format="yyyy-MM-dd HH:mm:ss"
                :placeholder="$t('adminPayFeeDetail.search.feeEndTimePlaceholder')" class="w-full" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model="searchForm.payerObjName"
                :placeholder="$t('adminPayFeeDetail.search.payerObjNamePlaceholder')" clearable />
            </el-col>
          </el-row>

          <template #actions>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">
              {{ $t('common.search') }}
            </el-button>
            <el-button icon="el-icon-refresh-left" @click="handleReset">
              {{ $t('common.reset') }}
            </el-button>
          </template>
        </ds-search-panel>

        <!-- (3)(4)(5)(6) Toolbar + DataTable + 合计区 + PaginationBar -->
        <div class="ds-panel">
          <ds-toolbar :total="pagination.total" :density="density"
            :show-column-setting="false" :show-refresh="true"
            @update:density="density = $event" @refresh="fetchData">
            <template #left>
              <span class="ds-panel__title">{{ $t('adminPayFeeDetail.list.title') }}</span>
              <el-tooltip :content="$t('adminPayFeeDetail.list.tooltip')" placement="top">
                <i class="el-icon-info ds-panel__info" />
              </el-tooltip>
            </template>
          </ds-toolbar>

          <ds-data-table :data="tableData" :loading="loading" :error="hasError"
            :error-message="errorMessage" :density="density" border
            :empty-description="$t('adminPayFeeDetail.page.empty')" @retry="fetchData">
            <el-table-column prop="oId" :label="$t('adminPayFeeDetail.table.orderId')" align="center" width="160" />
            <el-table-column :label="$t('adminPayFeeDetail.table.payerInfo')" align="left" width="160">
              <template slot-scope="scope">
                <div>{{ scope.row.payerObjName }}/{{ scope.row.ownerName }}</div>
                <div class="ds-cell-sub">{{ scope.row.communityName }}</div>
              </template>
            </el-table-column>
            <el-table-column :label="$t('adminPayFeeDetail.table.feeInfo')" align="left" min-width="160">
              <template slot-scope="scope">
                {{ scope.row.feeTypeCdName }}>{{ scope.row.feeName }}
              </template>
            </el-table-column>
            <el-table-column :label="$t('adminPayFeeDetail.table.feeStatus')" align="center" width="110">
              <template slot-scope="scope">
                <ds-status-tag :type="resolveFeeStatus(scope.row)" :label="scope.row.stateName" />
              </template>
            </el-table-column>
            <el-table-column prop="primeRate" :label="$t('adminPayFeeDetail.table.paymentMethod')" align="center" width="110" />
            <el-table-column :label="$t('adminPayFeeDetail.table.feePeriod')" align="center" width="150">
              <template slot-scope="scope">
                <div>{{ formatDate(scope.row.startTime) }}</div>
                <div class="ds-cell-sub">~ {{ formatDate(scope.row.endTime) }}</div>
              </template>
            </el-table-column>
            <el-table-column :label="$t('adminPayFeeDetail.table.paymentTime')" align="center" width="150">
              <template slot-scope="scope">
                {{ formatDateTime(scope.row.createTime) }}
              </template>
            </el-table-column>
            <el-table-column prop="cashierName" :label="$t('adminPayFeeDetail.table.cashier')" align="center" width="100" />
            <el-table-column :label="$t('adminPayFeeDetail.table.payableAmount')" align="right" width="130">
              <template slot-scope="scope">
                <div class="ds-num">{{ scope.row.payableAmount }}</div>
                <div class="ds-num ds-cell-sub">{{ scope.row.receivableAmount }}</div>
              </template>
            </el-table-column>
            <el-table-column :label="$t('adminPayFeeDetail.table.receivedAmount')" align="right" width="120">
              <template slot-scope="scope">
                <span class="ds-num">{{ formatNumber(scope.row.receivedAmount) }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('adminPayFeeDetail.table.withholdAmount')" align="right" width="120">
              <template slot-scope="scope">
                <span class="ds-num">{{ formatNumber(scope.row.withholdAmount) }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('adminPayFeeDetail.table.discountAmount')" align="right" width="130">
              <template slot-scope="scope">
                <div class="ds-num">{{ scope.row.preferentialAmount }}</div>
                <div class="ds-num ds-cell-sub">{{ scope.row.deductionAmount }}</div>
              </template>
            </el-table-column>
            <el-table-column :label="$t('adminPayFeeDetail.table.giftAmount')" align="right" width="110">
              <template slot-scope="scope">
                <span class="ds-num">{{ formatNumber(scope.row.giftAmount) }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('adminPayFeeDetail.table.lateFee')" align="right" width="110">
              <template slot-scope="scope">
                <span class="ds-num">{{ formatNumber(scope.row.lateFee) }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('adminPayFeeDetail.table.area')" align="right" width="100">
              <template slot-scope="scope">
                <span class="ds-num">{{ formatNumber(scope.row.builtUpArea, 0) }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="psName" :label="$t('adminPayFeeDetail.table.parkingSpace')" align="center" width="110" />
            <el-table-column :label="$t('adminPayFeeDetail.table.remark')" align="left" min-width="160" show-overflow-tooltip>
              <template slot-scope="scope">
                {{ scope.row.remark || '-' }}
              </template>
            </el-table-column>
          </ds-data-table>

          <!-- 合计区：小计 / 大计口径与改造前完全一致（均由后端 sumTotal 提供） -->
          <div class="ds-summary">
            <div class="ds-summary__row">
              <div class="ds-summary__cell ds-summary__cell--head">{{ $t('adminPayFeeDetail.summary.subtotal') }}</div>
              <div class="ds-summary__cell">
                <span class="ds-summary__label">{{ $t('adminPayFeeDetail.summary.receivable') }}</span>
                <span class="ds-num">{{ summary.totalReceivableAmount }}</span>
              </div>
              <div class="ds-summary__cell">
                <span class="ds-summary__label">{{ $t('adminPayFeeDetail.summary.received') }}</span>
                <span class="ds-num">{{ summary.totalReceivedAmount }}</span>
              </div>
              <div class="ds-summary__cell">
                <span class="ds-summary__label">{{ $t('adminPayFeeDetail.summary.discount') }}</span>
                <span class="ds-num">{{ summary.totalPreferentialAmount }}</span>
              </div>
              <div class="ds-summary__cell">
                <span class="ds-summary__label">{{ $t('adminPayFeeDetail.summary.deduction') }}</span>
                <span class="ds-num">{{ summary.totalDeductionAmount }}</span>
              </div>
              <div class="ds-summary__cell">
                <span class="ds-summary__label">{{ $t('adminPayFeeDetail.summary.gift') }}</span>
                <span class="ds-num">{{ summary.totalGiftAmount }}</span>
              </div>
              <div class="ds-summary__cell">
                <span class="ds-summary__label">{{ $t('adminPayFeeDetail.summary.lateFee') }}</span>
                <span class="ds-num">{{ summary.totalLateFee }}</span>
              </div>
            </div>
            <div class="ds-summary__row ds-summary__row--total">
              <div class="ds-summary__cell ds-summary__cell--head">{{ $t('adminPayFeeDetail.summary.total') }}</div>
              <div class="ds-summary__cell">
                <span class="ds-summary__label">{{ $t('adminPayFeeDetail.summary.receivable') }}</span>
                <span class="ds-num">{{ summary.allReceivableAmount }}</span>
              </div>
              <div class="ds-summary__cell">
                <span class="ds-summary__label">{{ $t('adminPayFeeDetail.summary.received') }}</span>
                <span class="ds-num">{{ summary.allReceivedAmount }}</span>
              </div>
              <div class="ds-summary__cell">
                <span class="ds-summary__label">{{ $t('adminPayFeeDetail.summary.discount') }}</span>
                <span class="ds-num">{{ summary.allPreferentialAmount }}</span>
              </div>
              <div class="ds-summary__cell">
                <span class="ds-summary__label">{{ $t('adminPayFeeDetail.summary.deduction') }}</span>
                <span class="ds-num">{{ summary.allDeductionAmount }}</span>
              </div>
              <div class="ds-summary__cell">
                <span class="ds-summary__label">{{ $t('adminPayFeeDetail.summary.gift') }}</span>
                <span class="ds-num">{{ summary.allGiftAmount }}</span>
              </div>
              <div class="ds-summary__cell">
                <span class="ds-summary__label">{{ $t('adminPayFeeDetail.summary.lateFee') }}</span>
                <span class="ds-num">{{ summary.allLateFee }}</span>
              </div>
            </div>
            <div class="ds-summary__notes">
              <div>{{ $t('adminPayFeeDetail.summary.discountDesc') }}</div>
              <div>{{ $t('adminPayFeeDetail.summary.deductionDesc') }}</div>
              <div>{{ $t('adminPayFeeDetail.summary.giftDesc') }}</div>
              <div>{{ $t('adminPayFeeDetail.summary.lateFeeDesc') }}</div>
            </div>
          </div>

          <ds-pagination-bar :total="pagination.total" :page="pagination.current" :size="pagination.size"
            :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange" />
        </div>
      </el-col>
    </el-row>
  </div>
</template>


<script>
import { queryAdminPayFeeDetail, exportFeeData } from '@/api/fee/adminPayFeeDetailApi'
import SelectAdminCommunity from '@/components/community/selectAdminCommunity'
import { getDict } from '@/api/community/communityApi'
import { dateFormat } from '@/utils/dateUtil'
import { formatNumber, formatDateTime } from '@/utils/format'
import { resolveStatus } from '@/utils/statusMap'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'AdminPayFeeDetailList',
  components: {
    SelectAdminCommunity,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsStatusTag,
    DsPaginationBar
  },
  data() {
    return {
      searchForm: {
        communityId: '',
        floorId: '',
        roomNum: '',
        payerObjName: '',
        configId: '',
        primeRate: '',
        state: '',
        feeTypeCd: '',
        startTime: '',
        endTime: '',
        feeStartTime: '',
        feeEndTime: '',
        page: 1,
        row: 10
      },
      tableData: [],
      paymentMethods: [],
      feeStatuses: [],
      feeTypes: [],
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      summary: {
        totalReceivableAmount: '0.00',
        totalReceivedAmount: '0.00',
        allReceivableAmount: '0.00',
        allReceivedAmount: '0.00',
        totalPreferentialAmount: '0.00',
        totalDeductionAmount: '0.00',
        totalLateFee: '0.00',
        totalVacantHousingDiscount: '0.00',
        totalVacantHousingReduction: '0.00',
        totalNoDeduction: '0.00',
        totalCashDeduction: '0.00',
        totalPointDeduction: '0.00',
        totalDiscountCouponDeduction: '0.00',
        allPreferentialAmount: '0.00',
        allDeductionAmount: '0.00',
        allLateFee: '0.00',
        allVacantHousingDiscount: '0.00',
        allVacantHousingReduction: '0.00',
        allGiftAmount: '0.00',
        allNoDeduction: '0.00',
        allCashDeduction: '0.00',
        allPointDeduction: '0.00',
        allDiscountCouponDeduction: '0.00',
        totalGiftAmount: '0.00'
      },
      pagination: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.loadDictData()
    this.fetchData()
  },
  methods: {
    async loadDictData() {
      this.paymentMethods = await getDict('pay_fee_detail', 'prime_rate')
      this.feeStatuses = await getDict('pay_fee_detail', 'state')
      this.feeTypes = await getDict('pay_fee_config', 'fee_type_cd')
    },
    async fetchData() {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const params = { ...this.searchForm }
        const res = await queryAdminPayFeeDetail(params)

        // 兼容后端在「无匹配数据」时返回 { code: 0, records: 0 } 而不带 data 字段的情况。
        // 原实现直接 data.map() 会在此处抛 TypeError，导致筛选后表格仍停留在上一次的数据，
        // 用户无法感知「筛选无结果」。这里做安全降级：缺 data 时按空数组处理。
        const list = Array.isArray(res.data) ? res.data : []
        const { total, sumTotal } = res

        this.tableData = list.map(item => ({
          ...item,
          lateFee: (item.lateFee * -1).toFixed(2)
        }))

        this.pagination.total = total || 0
        this.updateSummary(sumTotal || {})
      } catch (error) {
        // 保留原有静默处理语义：仅记录错误态，不弹 toast（避免影响既有使用体验）
        this.hasError = true
        this.errorMessage = this.$t('adminPayFeeDetail.page.loadFailed')
        console.error('Failed to load payment details:', error)
      } finally {
        this.loading = false
      }
    },
    updateSummary(sumTotal) {
      const defaultZero = '0.00'
      const s = sumTotal || {}

      this.summary = {
        totalReceivableAmount: s.totalReceivableAmount || defaultZero,
        totalReceivedAmount: s.totalReceivedAmount || defaultZero,
        allReceivableAmount: s.allReceivableAmount || defaultZero,
        allReceivedAmount: s.allReceivedAmount || defaultZero,
        totalPreferentialAmount: s.totalPreferentialAmount || defaultZero,
        totalDeductionAmount: s.totalDeductionAmount || defaultZero,
        totalLateFee: s.totalLateFee ? (s.totalLateFee * -1).toFixed(2) : defaultZero,
        allPreferentialAmount: s.allPreferentialAmount || defaultZero,
        allDeductionAmount: s.allDeductionAmount || defaultZero,
        allLateFee: s.allLateFee ? (s.allLateFee * -1).toFixed(2) : defaultZero,
        allGiftAmount: s.allGiftAmount || defaultZero,
        totalGiftAmount: s.totalGiftAmount || defaultZero
      }
    },
    handleSearch() {
      this.searchForm.page = 1
      this.pagination.current = 1
      this.fetchData()
    },
    handleReset() {
      this.searchForm = {
        communityId: this.searchForm.communityId,
        floorId: '',
        roomNum: '',
        payerObjName: '',
        configId: '',
        primeRate: '',
        state: '',
        feeTypeCd: '',
        startTime: '',
        endTime: '',
        feeStartTime: '',
        feeEndTime: '',
        page: 1,
        row: this.searchForm.row
      }
      this.pagination.current = 1
      this.fetchData()
    },
    handleCommunityChange(community) {
      this.searchForm.communityId = community.communityId
      this.fetchData()
    },
    handleSizeChange(size) {
      this.searchForm.row = size
      // 修复：原实现只改 searchForm.row，从未回写 pagination.size / current，
      // 导致分页器「每页条数」与高亮页码与实际请求状态失步。
      this.pagination.size = size
      this.pagination.current = 1
      this.fetchData()
    },
    handleCurrentChange(page) {
      this.searchForm.page = page
      // 修复：回写当前页，保证分页器高亮与实际请求页码一致。
      this.pagination.current = page
      this.fetchData()
    },
    async handleExport() {
      try {
        const params = {
          ...this.searchForm,
          pagePath: 'adminPayFeeDetail'
        }
        await exportFeeData(params)
        this.$message.success(this.$t('common.operationSuccess'))
        // 这里可以添加跳转到下载页面的逻辑
        // this.$router.push('/pages/property/downloadTempFile?tab=下载中心')
      } catch (error) {
        this.$message.error(this.$t('adminPayFeeDetail.exportError'))
      }
    },
    formatDate(date) {
      if (!date) return '-'
      return dateFormat(date)
    },
    /**
     * 金额/数量格式化（只改展示，不改原值）
     */
    formatNumber(value, decimals = 2) {
      return formatNumber(value, decimals)
    },
    /**
     * 缴费时间格式化（后端返回可能是 "yyyy-MM-dd HH:mm:ss" 字符串）
     */
    formatDateTime(value) {
      return formatDateTime(value)
    },
    /**
     * 费用状态语义映射：优先用后端 state 命中 fee 字典，
     * 未命中时用 stateName 做语义推断，保证「圆点 + 文字」同时出现（WCAG 1.4.1）。
     */
    resolveFeeStatus(row) {
      if (!row) return 'muted'
      const status = resolveStatus(row.state, {
        domain: 'fee',
        text: row.stateName || ''
      })
      return status.type
    }
  }
}
</script>

<style lang="scss" scoped>
.admin-pay-fee-detail-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);

  /* 左侧小区树卡片 */
  ::v-deep .community-selector {
    border-radius: var(--ds-radius-md);
    border: 1px solid var(--ds-gray-300);
    box-shadow: var(--ds-shadow-sm);
    max-height: calc(100vh - 140px);
    overflow-y: auto;

    .community-list li {
      font-size: var(--ds-font-size-sm);
      color: var(--ds-gray-700);
      transition: background-color var(--ds-transition-fast),
                  color var(--ds-transition-fast);

      &:hover {
        background-color: rgba(var(--ds-primary-rgb), 0.08);
        color: var(--ds-primary);
      }

      &.selected {
        background-color: var(--ds-primary);
        color: #fff;
      }
    }
  }
}

/* 表格 + 合计 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }

  &__info {
    color: var(--ds-gray-400);
    cursor: pointer;
    transition: color var(--ds-transition-fast);

    &:hover {
      color: var(--ds-primary);
    }
  }
}

/* 表格次要文字（小区名、应收金额等） */
.ds-cell-sub {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
}

/* 合计区 */
.ds-summary {
  padding: var(--ds-spacing-md) var(--ds-spacing-lg);
  background: var(--ds-gray-50);
  border-top: 1px solid var(--ds-gray-200);

  &__row {
    display: flex;
    flex-wrap: wrap;
    gap: var(--ds-spacing-md);
    padding: var(--ds-spacing-xs) 0;

    &--total {
      border-top: 1px dashed var(--ds-gray-300);
      margin-top: var(--ds-spacing-xs);
      padding-top: var(--ds-spacing-sm);

      .ds-summary__cell {
        font-weight: 600;
      }
    }
  }

  &__cell {
    display: flex;
    align-items: baseline;
    gap: var(--ds-spacing-xs);
    min-width: 120px;

    &--head {
      font-weight: 600;
      color: var(--ds-gray-800);
    }
  }

  &__label {
    font-size: var(--ds-font-size-xs);
    color: var(--ds-gray-500);
  }

  &__notes {
    margin-top: var(--ds-spacing-sm);
    padding-top: var(--ds-spacing-sm);
    border-top: 1px solid var(--ds-gray-200);
    font-size: var(--ds-font-size-xs);
    color: var(--ds-gray-500);
    line-height: 1.8;
  }
}
</style>
